import { selectAppTypesGroups, selectIsLogout, selectMarkets } from "../../state/selectors";
import { dispatch, getState } from "../../state/store";
import {
  resetCognitoUser,
  resetGroupsConfigs,
  resetLocations,
  resetUserRoles,
  setChallengedUser,
  setEventsFetched,
  setIsLogout,
  setIsMandatoryDataFetched,
  setLocation,
  setMarket,
  setMarketEventsFetched,
  setMarketIso,
  setUserAppTypes,
  setUserMarketFilter,
  setUserMarkets,
  setUserRoles
} from "../../state/ducks";

import {
  appTypesService,
  appUserService,
  awsAmplifyService,
  groupsService,
  httpClient,
  locationsService,
  log,
  marketsService,
  navigationService,
  storageService
} from "../index";
import {
  appTypesNames,
  appUserRoles,
  awsAuthErrorTypes,
  marketNames,
  routePaths
} from "../../consts";

const apiUrl = process.env.REACT_APP_API_URL;

const getAuthorizedUser = async () => {
  const user = await awsAmplifyService.currentAuthenticatedUser();
  const groups = user.signInUserSession.idToken.payload["cognito:groups"];
  const hasAppTypes = user?.attributes ? !!user.attributes["custom:app_types"] : false;
  const refreshToken = user?.signInUserSession?.refreshToken?.token;

  const isLogout = selectIsLogout()(getState());

  if (
    !hasAppTypes &&
    !groups.includes(appUserRoles.Admin) &&
    !groups.includes(appUserRoles.FleetManager)
  ) {
    dispatch(setIsLogout(true));
    await logOut();

    throw new Error(awsAuthErrorTypes.USER_APP_TYPES_AVAILABILITY);
  }

  if (isLogout) {
    dispatch(setIsLogout(false));
  } else {
    await createSession(refreshToken);

    return user;
  }
};

const createSession = async (refreshToken) => {
  try {
    const url = `${apiUrl}/session-management/create-session`;

    await httpClient.post(url, {
      data: {
        refreshToken: refreshToken
      }
    });
  } catch (e) {
    log.error("createSession error", e);
  }
};

const revokeToken = async () => {
  try {
    const user = await awsAmplifyService.currentAuthenticatedUser();
    const refreshToken = user.signInUserSession.refreshToken.token;

    const url = `${apiUrl}/admin/logout`;
    await httpClient.post(url, {
      data: {
        refreshToken: refreshToken
      }
    });
  } catch (e) {
    log.error("revokeToken error", e);
  }
};

const logOut = async () => {
  await revokeToken();

  try {
    await awsAmplifyService.signOut();

    dispatch(resetCognitoUser());
    dispatch(setChallengedUser(null));
    storageService.removeMarketFromStorage();
    dispatch(setMarketIso(null));
    dispatch(setUserAppTypes(null));
    dispatch(resetUserRoles());
    storageService.removeAppTypeFromStorage();
    dispatch(resetLocations());
    dispatch(resetGroupsConfigs());
    dispatch(setUserMarketFilter(null));
    dispatch(setIsMandatoryDataFetched(false));
  } catch (e) {
    log.error("Logout error", e);
  }
};

const validatePasswordByFields = (password) => {
  const templateSymbol = /[!"#$%&'()*+,\-./:;<=>?@[\]^_`{}|~]/;
  const templateBigSymbol = /(?=.*[A-Z])/;
  const templateNumber = /(?=.*\d)/;

  return {
    isValidPasswordSymbol: password.match(templateSymbol)?.length > 0,
    isValidPasswordBigSymbol: password.match(templateBigSymbol)?.length > 0,
    isValidPasswordNumber: password.match(templateNumber)?.length > 0
  };
};

const generateOtpAuthUrl = (username, secret) => {
  return `otpauth://totp/Innovations%20Admin%20Portal:${username}?secret=${secret}&issuer=Innovations%20Admin%20Portal`;
};

const setAppTypes = async () => {
  await appTypesService.fetchAppTypes();
  const user = await awsAmplifyService.currentAuthenticatedUser();

  let appTypes = selectAppTypesGroups()(getState());

  appTypes = appTypes
    ? appTypes.map((appType) => appType.appType).join(",")
    : user?.attributes["custom:app_types"] || "";

  const prepearedAppTypes = appTypes.split(",").filter((appType) => appType);

  dispatch(setUserAppTypes([appTypesNames.allApps, ...prepearedAppTypes]));
};

const setMarkets = async () => {
  await marketsService.fetchMarkets();
  const user = await awsAmplifyService.currentAuthenticatedUser();

  let markets = selectMarkets()(getState());

  markets = markets
    ? markets.map((market) => market.market).join(",")
    : user?.attributes["custom:market"];

  if (markets) {
    const userMarkets = markets.replaceAll(" ", "").split(",");
    const storageMarket = storageService.getMarketFromStorage()?.replaceAll(" ", "");

    userMarkets.unshift(marketNames.allMarkets);

    dispatch(setUserMarkets(userMarkets));

    if (!userMarkets) {
      return;
    }

    // fetch location data for each available market
    if (
      appUserService.hasRole(appUserRoles.Admin) ||
      appUserService.hasRole(appUserRoles.ApplicationManager) ||
      appUserService.hasRole(appUserRoles.ContentManager)
    ) {
      userMarkets.slice(1).forEach((market) => {
        locationsService.fetchLocations(market);
      });
    }

    let marketIso;

    if (userMarkets?.length > 1) {
      marketIso = marketNames.allMarkets;
    } else if (storageMarket && (userMarkets.includes(storageMarket) || appUserService.isAdmin())) {
      marketIso = storageMarket;
    }

    if (!marketIso) {
      marketIso = userMarkets[0];
    }

    dispatch(setMarketIso(marketIso));
    dispatch(setUserMarketFilter(marketIso));

    // set default market and location for dashboard filters and reset events data
    dispatch(setMarket({ id: marketIso, value: marketIso }));
    dispatch(setLocation(null));
    dispatch(setEventsFetched(false));
    dispatch(setMarketEventsFetched(false));
  }
};

const setGroups = async () => {
  const user = await awsAmplifyService.currentAuthenticatedUser();
  const userAppTypes = user?.attributes ? user.attributes["custom:app_types"] || "" : "";

  const prepearedAppTypes = userAppTypes
    .replaceAll(" ", "")
    .split(",")
    .filter((appType) => appType);

  const appTypes = selectAppTypesGroups()(getState());

  if (appUserService.isAdmin()) {
    appTypes.forEach((appType) => {
      groupsService.fetchGroup(appType.appType);
    });
  } else if (
    appUserService.hasRole(appUserRoles.ApplicationManager) ||
    appUserService.hasRole(appUserRoles.ContentManager)
  ) {
    prepearedAppTypes.forEach(groupsService.fetchGroup);
  }
};

const getAwaitUserGroup = async () => {
  try {
    let session = await awsAmplifyService.currentSession();
    const groups = await session.getAccessToken().decodePayload()["cognito:groups"];
    dispatch(setUserRoles(groups));
    storageService.setUserClaimsToStorage(groups);
    return groups;
  } catch (e) {
    await logOut();
    navigationService.replace(`${routePaths.LOGIN}`);
  }
};

const authService = {
  getAuthorizedUser,
  revokeToken,
  logOut,
  validatePasswordByFields,
  generateOtpAuthUrl,
  setAppTypes,
  setMarkets,
  setGroups,
  getAwaitUserGroup
};

export default authService;
